import React, { useState, useEffect } from 'react';

/**
 * LandingPage — SIFISO — The Golden Hand
 *
 * The five steps shown as ONE journey arc, not five separate doors:
 *   1 LEARN (Python) → 2 AI → 3 MASTER (Quiz) → 4 BUILD (Startup) → 5 TEACH (Commons)
 *
 * Progress-aware + soft-gated: each step reads real localStorage progress, shows
 * Done / In progress / Up next / Locked, and unlocks the next step once the
 * current one is started. "Explore everything" lifts the gates (content already
 * exists, so nothing is ever truly hidden). Routing is unchanged — each step
 * still calls its onEnterGolden* callback.
 */

const GOLD = '#D4AF37';
const GOLD_BRIGHT = '#FFD700';
const BG = '#0A0F1C';
const CARD = '#1A1F2E';

// Safe localStorage helpers (landing renders before anything is guaranteed).
const readArray = (key) => {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

function LandingPage({ onEnterGoldenLearn, onEnterGoldenAI, onEnterGoldenQuiz, onEnterGoldenStartup, onEnterGoldenCommons }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [exploreAll, setExploreAll] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Real progress ──────────────────────────────────────────────────────────
  const learnArr = readArray('sifiso-completed');
  const aiArr = readArray('sifiso-ai-completed');
  const quizCount = readArray('sifiso-quiz-history').length;

  const learnDone = learnArr.filter(Boolean).length;
  const aiDone = aiArr.filter(Boolean).length;
  const pLearn = learnArr.length ? learnDone / learnArr.length : 0;
  const pAi = aiArr.length ? aiDone / aiArr.length : 0;

  const learnStarted = learnDone > 0;
  const aiStarted = aiDone > 0;
  const quizStarted = quizCount > 0;

  // ── The five steps ───────────────────────────────────────────────────────────
  const steps = [
    {
      id: 'learn', n: 1, verb: 'LEARN', name: 'GOLDEN LEARN', subtitle: 'Python',
      role: 'Learn the skill',
      description: 'Write real Python in your browser and learn to think in systems — the foundation everything else stands on.',
      onClick: onEnterGoldenLearn,
      frac: pLearn,
      meta: learnArr.length ? `${learnDone} / ${learnArr.length} levels` : null,
      complete: learnArr.length > 0 && learnDone === learnArr.length,
      started: learnStarted,
    },
    {
      id: 'ai', n: 2, verb: 'AI', name: 'GOLDEN AI', subtitle: 'Learning AI',
      role: 'Add judgment',
      description: 'Learn to think with AI, not through it — and catch it where it is confidently wrong.',
      onClick: onEnterGoldenAI,
      frac: pAi,
      meta: aiArr.length ? `${aiDone} / ${aiArr.length} levels` : null,
      complete: aiArr.length > 0 && aiDone === aiArr.length,
      started: aiStarted,
    },
    {
      id: 'quiz', n: 3, verb: 'MASTER', name: 'GOLDEN QUIZ', subtitle: 'Quiz Master',
      role: 'Prove your mastery',
      description: 'Test yourself across 7 categories and 3 levels until the hidden gaps disappear.',
      onClick: onEnterGoldenQuiz,
      frac: quizStarted ? Math.min(quizCount / 5, 1) : 0,
      meta: quizStarted ? `${quizCount} quiz${quizCount === 1 ? '' : 'zes'} taken` : null,
      complete: false,
      started: quizStarted,
    },
    {
      id: 'startup', n: 4, verb: 'BUILD', name: 'GOLDEN STARTUP', subtitle: '1 Million Startups in 12 Months',
      role: 'Turn it into a living',
      description: 'Turn what you know into income and real problem-solving — livelihood literacy, not theory.',
      onClick: onEnterGoldenStartup,
      frac: 0, meta: null, complete: false, started: false,
    },
    {
      id: 'commons', n: 5, verb: 'TEACH', name: 'GOLDEN COMMONS', subtitle: 'Each One, Teach One',
      role: 'Pass it on',
      description: 'Teach the next person and close the loop — Ubuntu: knowledge that circulates instead of dying with you.',
      onClick: onEnterGoldenCommons,
      frac: 0, meta: null, complete: false, started: false,
    },
  ];

  // ── Soft gating: a step unlocks once the previous one is started ──────────────
  const unlocked = [
    true,
    learnStarted,
    learnStarted && aiStarted,
    learnStarted && aiStarted && quizStarted,
    learnStarted && aiStarted && quizStarted, // Teach opens with Build (experiential)
  ].map((u) => u || exploreAll);

  // The recommended step = first unlocked step that isn't complete.
  const currentIndex = steps.findIndex((s, i) => unlocked[i] && !s.complete);

  const statusOf = (i) => {
    if (!unlocked[i]) return 'locked';
    if (steps[i].complete) return 'done';
    if (i === currentIndex) return 'current';
    return 'open';
  };

  const doneCount = steps.filter((s) => s.complete).length;

  const STATUS_BADGE = {
    done: { text: 'Done', bg: 'rgba(34,197,94,0.15)', fg: '#4ade80' },
    current: { text: 'Up next', bg: 'rgba(212,175,55,0.18)', fg: GOLD_BRIGHT },
    open: { text: 'Open', bg: 'rgba(96,165,250,0.15)', fg: '#60a5fa' },
    locked: { text: 'Locked', bg: 'rgba(148,163,184,0.12)', fg: '#94a3b8' },
  };

  const ctaLabel = (i, status, started) => {
    if (status === 'locked') return `🔒 Finish ${steps[i - 1].verb} first`;
    if (status === 'done') return 'Revisit →';
    if (status === 'current') return started ? 'Continue →' : 'Start here →';
    return 'Open →';
  };

  const circleSize = isMobile ? 40 : 48;
  const railLeft = circleSize / 2 - 1;

  return (
    <div style={{ backgroundColor: BG, color: '#F5F5F5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 20px' }}>
        {/* Header */}
        <header style={{ padding: '30px 0 14px' }}>
          <h1 style={{ fontSize: isMobile ? '1.8rem' : '2.8rem', fontWeight: 800, marginBottom: 8, letterSpacing: '-0.5px' }}>
            <span style={{ color: GOLD }}>SIFISO</span>{' '}
            <span style={{ color: GOLD_BRIGHT, fontSize: isMobile ? '1.3rem' : '2rem', fontWeight: 600, fontStyle: 'italic' }}>The Golden Hand</span>
          </h1>
          <p style={{ fontSize: isMobile ? '0.95rem' : '1.15rem', color: '#C9C9C9', fontWeight: 300, lineHeight: 1.5 }}>
            Five steps. One journey — from your first line of code to your own business, and back to teach the next person.
          </p>
        </header>

        {/* Hero Section */}
        <section style={{
          position: 'relative',
          height: isMobile ? '350px' : '450px',
          borderRadius: '20px',
          overflow: 'hidden',
          marginBottom: '50px',
          boxShadow: '0 15px 30px rgba(0, 0, 0, 0.5)'
        }}>
          {/* Background Image */}
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            backgroundImage: 'url(https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80)',
            backgroundSize: 'cover', backgroundPosition: 'center', filter: 'brightness(0.7)'
          }} />
          {/* Overlay */}
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            background: 'linear-gradient(135deg, rgba(10, 15, 28, 0.85) 0%, rgba(212, 175, 55, 0.2) 100%)',
            display: 'flex', alignItems: 'center', padding: isMobile ? '0 20px' : '0 60px'
          }}>
            <div style={{ maxWidth: isMobile ? '100%' : '700px' }}>
              <h2 style={{ fontSize: isMobile ? '1.8rem' : '3.2rem', fontWeight: 800, marginBottom: isMobile ? '15px' : '20px', lineHeight: 1.2 }}>
                Building Capacity That Lasts Beyond You
              </h2>
              <p style={{ fontSize: isMobile ? '1rem' : '1.3rem', fontWeight: 300, marginBottom: isMobile ? '20px' : '30px', opacity: 0.9, display: isMobile ? 'none' : 'block' }}>
                The Golden Hand is a closed-loop system. What you learn, you test. What you master, you apply. What you build, you teach. Knowledge circulates, capacity regenerates.
              </p>
              {/* Stats */}
              <div style={{ display: 'flex', gap: isMobile ? '20px' : '30px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: 700, color: GOLD_BRIGHT }}>5</span>
                  <span style={{ fontSize: isMobile ? '0.7rem' : '0.9rem', color: GOLD, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Capacities</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: 700, color: GOLD_BRIGHT }}>1</span>
                  <span style={{ fontSize: isMobile ? '0.7rem' : '0.9rem', color: GOLD, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Loop</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: 700, color: GOLD_BRIGHT }}>∞</span>
                  <span style={{ fontSize: isMobile ? '0.7rem' : '0.9rem', color: GOLD, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Circulation</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section heading for the journey */}
        <h2 style={{ fontSize: isMobile ? '1.4rem' : '1.8rem', fontWeight: 700, textAlign: 'center', marginBottom: 6 }}>
          THE FIVE FINGERS
        </h2>
        <p style={{ textAlign: 'center', color: '#A0A0A0', fontSize: '0.95rem', maxWidth: 620, margin: '0 auto 18px', lineHeight: 1.6 }}>
          One journey, five steps — each finger a capacity, together a loop that turns understanding into ability, ability into livelihood, and livelihood into knowledge you pass forward.
        </p>

        {/* Arc ribbon */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap',
          gap: isMobile ? '4px' : '10px', padding: '14px 0 6px',
        }}>
          {steps.map((s, i) => (
            <React.Fragment key={s.id}>
              <span style={{
                fontSize: isMobile ? '0.7rem' : '0.9rem', fontWeight: 700, letterSpacing: '1px',
                color: statusOf(i) === 'locked' ? '#5a6478' : GOLD,
              }}>{s.verb}</span>
              {i < steps.length - 1 && <span style={{ color: '#4a5268', fontSize: isMobile ? '0.7rem' : '0.9rem' }}>→</span>}
            </React.Fragment>
          ))}
        </div>

        {/* Overall progress */}
        <div style={{ textAlign: 'center', margin: '6px 0 30px' }}>
          <span style={{ fontSize: '0.8rem', color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Your journey — <span style={{ color: GOLD_BRIGHT, fontWeight: 700 }}>{doneCount}</span> of 5 steps complete
          </span>
        </div>

        {/* The journey arc (vertical timeline) */}
        <section style={{ position: 'relative', marginBottom: 50 }}>
          {/* connecting rail behind the circles */}
          <div style={{
            position: 'absolute', left: railLeft, top: 28, bottom: 28, width: 2,
            background: `linear-gradient(${GOLD}, rgba(212,175,55,0.12))`,
          }} />

          {steps.map((s, i) => {
            const status = statusOf(i);
            const badge = STATUS_BADGE[status];
            const clickable = status !== 'locked' && s.onClick;
            const isDone = status === 'done';
            const isCurrent = status === 'current';
            return (
              <div key={s.id} style={{ display: 'flex', gap: isMobile ? 14 : 20, marginBottom: 18, position: 'relative' }}>
                {/* Step circle */}
                <div style={{ flexShrink: 0, width: circleSize, display: 'flex', justifyContent: 'center' }}>
                  <div style={{
                    width: circleSize, height: circleSize, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: isMobile ? '1rem' : '1.15rem',
                    backgroundColor: isDone ? GOLD : isCurrent ? 'rgba(212,175,55,0.16)' : CARD,
                    color: isDone ? '#0A0F1C' : status === 'locked' ? '#5a6478' : GOLD_BRIGHT,
                    border: `2px solid ${isCurrent ? GOLD_BRIGHT : isDone ? GOLD : 'rgba(212,175,55,0.25)'}`,
                    boxShadow: isCurrent ? `0 0 16px rgba(255,215,0,0.45)` : 'none',
                    zIndex: 1,
                  }}>
                    {isDone ? '✓' : status === 'locked' ? '🔒' : s.n}
                  </div>
                </div>

                {/* Step card */}
                <div
                  onClick={clickable ? s.onClick : undefined}
                  style={{
                    flex: 1, backgroundColor: CARD, borderRadius: 16, padding: isMobile ? '16px' : '20px 24px',
                    border: `1px solid ${isCurrent ? 'rgba(255,215,0,0.4)' : 'rgba(212,175,55,0.1)'}`,
                    opacity: status === 'locked' ? 0.55 : 1,
                    cursor: clickable ? 'pointer' : 'default',
                    transition: 'all 0.25s ease',
                  }}
                  onMouseEnter={(e) => { if (clickable) { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)'; } }}
                  onMouseLeave={(e) => { if (clickable) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = isCurrent ? 'rgba(255,215,0,0.4)' : 'rgba(212,175,55,0.1)'; } }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1.5px', color: '#8A8A8A', textTransform: 'uppercase' }}>
                        Step {s.n} · {s.role}
                      </div>
                      <h3 style={{ fontSize: isMobile ? '1.15rem' : '1.35rem', fontWeight: 700, color: GOLD, margin: '2px 0' }}>{s.name}</h3>
                      <div style={{ fontSize: '0.82rem', color: GOLD_BRIGHT, fontStyle: 'italic' }}>{s.subtitle}</div>
                    </div>
                    <span style={{ fontSize: '0.7rem', padding: '4px 10px', borderRadius: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: badge.bg, color: badge.fg, whiteSpace: 'nowrap' }}>
                      {badge.text}
                    </span>
                  </div>

                  <p style={{ fontSize: '0.9rem', color: '#A0A0A0', lineHeight: 1.5, margin: '0 0 14px' }}>{s.description}</p>

                  {/* progress bar (measurable steps) */}
                  {(s.frac > 0 || s.meta) && status !== 'locked' && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ height: 6, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round(s.frac * 100)}%`, height: '100%', background: `linear-gradient(to right, ${GOLD}, ${GOLD_BRIGHT})` }} />
                      </div>
                      {s.meta && <div style={{ fontSize: '0.72rem', color: '#8A8A8A', marginTop: 4 }}>{s.meta}</div>}
                    </div>
                  )}

                  {/* CTA */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      onClick={clickable ? (e) => { e.stopPropagation(); s.onClick(); } : undefined}
                      disabled={!clickable}
                      style={{
                        backgroundColor: status === 'current' ? GOLD : 'transparent',
                        color: status === 'current' ? '#0A0F1C' : status === 'locked' ? '#5a6478' : GOLD_BRIGHT,
                        border: status === 'current' ? 'none' : `1px solid rgba(212,175,55,0.4)`,
                        padding: '9px 18px', borderRadius: 10, fontSize: '0.85rem', fontWeight: 700,
                        cursor: clickable ? 'pointer' : 'not-allowed', letterSpacing: '0.3px',
                      }}
                    >
                      {ctaLabel(i, status, s.started)}
                    </button>
                    {status === 'locked' && (
                      <span style={{ fontSize: '0.75rem', color: '#6b7488', fontStyle: 'italic' }}>
                        unlocks after you start {steps[i - 1].verb}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Explore toggle */}
        <div style={{ textAlign: 'center', marginBottom: 50 }}>
          <button
            onClick={() => setExploreAll((v) => !v)}
            style={{
              background: 'transparent', border: `1px solid rgba(212,175,55,0.3)`, color: '#A0A0A0',
              padding: '8px 18px', borderRadius: 20, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 500,
            }}
          >
            {exploreAll ? '↩ Follow the journey in order' : '🔓 Just exploring? Open all five steps'}
          </button>
        </div>

        {/* Footer */}
        <footer style={{ padding: '20px 0 40px', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.05)', color: '#A0A0A0', fontSize: '0.9rem' }}>
          <p style={{ marginBottom: 8 }}>
            Powered by <strong style={{ color: GOLD }}>iKhaya AI</strong> — A division of <strong style={{ color: GOLD }}>ARISAN-SIFISO</strong>
          </p>
          <p style={{ marginBottom: 8 }}>© 2026 SIFISO — THE GOLDEN HAND. All rights reserved.</p>
          <p style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>Capacity that circulates, not content that accumulates.</p>
        </footer>
      </div>
    </div>
  );
}

export default LandingPage;
